import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import pluralize from 'pluralize'
import { IconSparkles } from '@tabler/icons-react'

import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextArea } from '@/components/forms/TextArea'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import { Progress } from '@/components/progress/Progress'
import { StatProgressBar } from '@/components/charts/StatProgressBar/StatProgressBar'
import { UnifiedDiff } from '@/components/diff/UnifiedDiff'
import { ChangedCallsList } from '@/views/QACopilot/optimize/ChangedCallsList'
import { OptimizationChip } from '@/views/PromptEngineering/components/PromptFeedbackCard'
import {
  POLLABLE_STATUSES,
  REGRESSION_SCOPE,
  RUN_STATUS,
} from '@/reducers/promptimizer/promptimizer.redux'
import {
  CALIBRATION_POLL_MS,
  CALIBRATION_RESULT,
  CALIBRATION_STOP,
  GUIDANCE_MAX_LENGTH,
} from '@/reducers/promptFeedback/promptFeedback.redux'
import {
  fetchFeedbackOptimizations,
  fetchFeedbackOptimizationDetail,
  requestCalibration,
} from '@/reducers/promptFeedback/promptFeedback.actions'

const VIEW = { DIFF: 'diff', FULL: 'full' }

const STOP_REASON_LABELS = {
  [CALIBRATION_STOP.SUCCESS]: 'every reported call fixed, behavior preserved',
  [CALIBRATION_STOP.ALREADY_SATISFIED]: 'the prompt already answers every reported call correctly',
  [CALIBRATION_STOP.MAX_ITERATIONS]: 'stopped after the attempt limit',
  [CALIBRATION_STOP.NO_GAIN]: 'stopped after attempts stopped improving',
  [CALIBRATION_STOP.COST_CEILING]: 'stopped at the cost ceiling',
}

const outcomeSentence = ({ outcome }) => {
  if (outcome?.result === CALIBRATION_RESULT.ALREADY_ADOPTED) {
    return 'Nothing left to fix'
  }
  if (outcome?.result === CALIBRATION_RESULT.AUTO_APPLIED) {
    return 'Applied automatically'
  }
  if (outcome?.stop_reason === CALIBRATION_STOP.ALREADY_SATISFIED) {
    return 'Nothing to change'
  }
  if (outcome?.result === CALIBRATION_RESULT.NO_CANDIDATE) {
    return 'No usable rewrite'
  }
  const attempts = outcome?.iterations
    ? ` after ${pluralize('attempt', outcome.iterations, true)}`
    : ''
  return `Ready for review${attempts}`
}

const explanation = (outcome) => {
  const reported = pluralize('reported call', outcome.feedback_total, true)
  const first =
    outcome.stop_reason === CALIBRATION_STOP.ALREADY_SATISFIED
      ? `The current prompt already answers all ${reported} correctly`
      : `This version fixes ${outcome.feedback_fixed ?? 0} of ${reported}`
  if (isNil(outcome.regression_total)) {
    return first
  }
  const unreported = `${pluralize('recent call', outcome.regression_total, true)} nobody reported`
  const changed = outcome.regression_total - outcome.regression_unchanged
  if (changed <= 0) {
    return `${first}. It leaves all ${unreported} unchanged`
  }
  const gate = outcome.regression_gate ?? {}
  const intended = Math.min(gate.intended_changes ?? 0, changed)
  const real = changed - intended
  const parts = [
    intended > 0 &&
      `${intended} ${intended === 1 ? 'has' : 'have'} the same issue as the reported calls, so changing them is expected`,
    real > 0 && (real === 1 ? '1 is a real change' : `${real} are real changes`),
  ].filter(Boolean)
  let variation = ''
  if (real > 0 && !isNil(gate.passed)) {
    variation = gate.passed
      ? ", within the prompt's normal variation"
      : ", more than the prompt's normal variation allows"
  }
  if (intended === 0) {
    return `${first}. Of the ${unreported}, it would change ${changed}${variation}`
  }
  return `${first}. Of the ${unreported}, it would change ${changed}: ${parts.join(', and ')}${variation}`
}

const FeedbackStat = ({ outcome }) => {
  const satisfied = outcome.stop_reason === CALIBRATION_STOP.ALREADY_SATISFIED
  return (
    <StatProgressBar
      label={satisfied ? 'Reported calls already answered correctly' : 'Reported calls fixed'}
      value={satisfied ? outcome.feedback_total : outcome.feedback_fixed ?? 0}
      total={outcome.feedback_total}
      variant="success"
    />
  )
}

const RegressionStat = ({ outcome }) => {
  const gate = outcome.regression_gate ?? {}
  const changed = outcome.regression_total - outcome.regression_unchanged
  const intended = Math.min(gate.intended_changes ?? 0, changed)
  const breakdown = [
    `${outcome.regression_unchanged} unchanged`,
    intended > 0 && `${intended} same issue as the reported calls`,
    pluralize('real change', changed - intended, true),
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <StatProgressBar
      label="Calls nobody reported: unchanged or expected to change"
      value={outcome.regression_unchanged + intended}
      total={outcome.regression_total}
      variant={gate.passed === false ? 'critical' : 'success'}
      footerLeft={breakdown}
      footerRight={
        !isNil(gate.passed) && (
          <Pill xs outlined success={gate.passed} critical={!gate.passed}>
            {gate.passed ? 'Regression check passed' : 'Regression check failed'}
          </Pill>
        )
      }
    />
  )
}

// Guidance goes with the next "Calibrate now" and is stored on that run — never posted as a task comment, which
// is the people's channel. Every later run on the task reads it too.
const HelpTheAgent = ({ value, onChange }) => (
  <FormField label="Help the Agent (optional)" data-testid="agent-help">
    <p className="muted-text">
      Recalibrate by sharing additional context or rules the reported calls don&apos;t show
    </p>
    <TextArea
      value={value}
      maxLength={GUIDANCE_MAX_LENGTH}
      placeholder="e.g. A partial payment still counts as a payment arrangement…"
      data-testid="agent-help-text"
      onChange={(event) => onChange(event.target.value)}
    />
  </FormField>
)

/**
 * What the background optimizer did with this task's feedback: the outcome and the prompt change.
 *
 * @import import { OptimizationReport } from '@/components/inbox/drawers/prompt-feedback/components/OptimizationReport'
 *
 * @param {object} props
 * @param {object} props.task The prompt-feedback task
 * @param {Function} [props.onUsePrompt] Stages the winner in the editor; omitted when there is no prompt to edit
 * @param {boolean} [props.canAct] False on a closed task: the report is read-only
 * @param {string} [props.currentPromptText] The prompt as it is now, to catch a run made on an older version
 * @param {boolean} [props.hasStagedChanges] Edits are already staged, which "Use this prompt" would replace
 * @param {boolean} [props.isEditingPrompt] An edit is open and not yet staged, which "Use this prompt" would replace
 * @param {boolean} [props.mustBePresent] The criterion's pass setting, to show answers as Pass/Fail/N/A
 */
export const OptimizationReport = ({
  task,
  onUsePrompt,
  canAct = true,
  currentPromptText,
  hasStagedChanges = false,
  isEditingPrompt = false,
  mustBePresent,
}) => {
  const dispatch = useDispatch()
  const summary = useSelector((state) => state.promptFeedback?.optimizations?.byTaskId?.[task?.id])
  const detail = useSelector(
    (state) => state.promptFeedback?.optimizations?.detail?.[summary?.optimization_id]
  )
  const detailLoading = useSelector(
    (state) => state.promptFeedback?.optimizations?.detailLoading === true
  )
  const [view, setView] = useState(VIEW.DIFF)
  const [showRegressionCalls, setShowRegressionCalls] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [guidance, setGuidance] = useState('')
  const [detailFailed, setDetailFailed] = useState(false)

  const inFlight = POLLABLE_STATUSES.includes(summary?.status)

  useEffect(() => {
    // Direct links open the drawer without the inbox's batch fetch having run.
    if (task?.id && isNil(summary)) {
      dispatch(fetchFeedbackOptimizations([task.id]))
    }
  }, [dispatch, task?.id, summary])

  useEffect(() => {
    if (!inFlight) {
      return undefined
    }
    const timer = setInterval(
      () => dispatch(fetchFeedbackOptimizations([task.id])),
      CALIBRATION_POLL_MS
    )
    return () => clearInterval(timer)
  }, [dispatch, inFlight, task?.id])

  const loadDetail = async () => {
    const data = await dispatch(fetchFeedbackOptimizationDetail(summary.optimization_id))
    setDetailFailed(isNil(data))
  }

  // A detail fetched mid-run is a snapshot: refetch once the run's status moves past it.
  useEffect(() => {
    if (summary?.optimization_id && !inFlight && detail?.status !== summary.status) {
      loadDetail()
    }
  }, [dispatch, summary?.optimization_id, summary?.status, inFlight, detail?.status])

  const header = (
    <h3 className="flex-align-center small-gap">
      <IconSparkles className="icon-svg status-magical" />
      Calibration Agent
    </h3>
  )

  const handleCalibrate = async () => {
    setRequesting(true)
    const started = await dispatch(requestCalibration(task.id, guidance.trim() || null))
    if (started) {
      setGuidance('')
    }
    setRequesting(false)
  }

  // Every terminal state offers this: a failed run is retryable, and a finished one can be re-run
  // against feedback that arrived since. Only queued/running withhold it, which the server enforces
  // anyway with a 409.
  const calibrateControls = (label) =>
    canAct && (
      <div className="optimization-report__calibrate field-stack">
        <HelpTheAgent value={guidance} onChange={setGuidance} />
        <Button
          icon={<IconSparkles className="status-magical" />}
          loading={requesting}
          disabled={requesting}
          onClick={handleCalibrate}
          data-testid="calibrate-now-button"
        >
          {label}
        </Button>
      </div>
    )

  if (isNil(summary)) {
    return (
      <section className="prompt-feedback-drawer__section optimization-report">
        {header}
        <p className="muted-text">
          The Calibration Agent can help rewrite this prompt based on the reported calls.
        </p>
        {calibrateControls('Calibrate now')}
      </section>
    )
  }

  if (inFlight) {
    return (
      <section className="prompt-feedback-drawer__section optimization-report">
        {header}
        <Progress magical indeterminate />
        <p className="muted-text">
          The Calibration Agent is optimizing this prompt against the reported calls — results will
          appear here.
        </p>
      </section>
    )
  }

  if ([RUN_STATUS.FAILED, RUN_STATUS.CANCELLED].includes(summary.status)) {
    return (
      <section className="prompt-feedback-drawer__section optimization-report">
        {header}
        <OptimizationChip optimization={summary} />
        {!isEmpty(detail?.failure_reason) && <p className="muted-text">{detail.failure_reason}</p>}
        {calibrateControls('Try again')}
      </section>
    )
  }

  if (isNil(detail) || detail.status !== summary.status) {
    return (
      <section className="prompt-feedback-drawer__section optimization-report">
        {header}
        {detailLoading && <Loader />}
        {detailFailed && !detailLoading && (
          <>
            <ErrorMessage content="Could not load this run's report." />
            <Button text onClick={loadDetail} data-testid="retry-agent-report">
              Try again
            </Button>
          </>
        )}
      </section>
    )
  }

  const { outcome } = detail
  const gatePassed = outcome?.regression_gate?.passed
  const hasCounts = !isNil(outcome?.feedback_total)
  const adopted = !isNil(detail.adopted_candidate_id)
  const nothingToChange =
    outcome?.stop_reason === CALIBRATION_STOP.ALREADY_SATISFIED ||
    outcome?.result === CALIBRATION_RESULT.ALREADY_ADOPTED
  const promptMoved =
    !isNil(currentPromptText) &&
    !isNil(detail.starting_prompt_text) &&
    currentPromptText !== detail.starting_prompt_text
  const canUse =
    canAct &&
    onUsePrompt &&
    !adopted &&
    !nothingToChange &&
    detail.winner_candidate_id &&
    !isEmpty(detail.winning_prompt_text)
  let useBlockedReason = null
  if (promptMoved) {
    useBlockedReason =
      'The prompt changed since this run, so this rewrite is of an older version — calibrate again'
  } else if (isEditingPrompt) {
    useBlockedReason = 'Stage or cancel your edit first — this would replace it'
  } else if (hasStagedChanges) {
    useBlockedReason = 'Clear your staged changes first — this would replace them'
  }

  return (
    <section
      className="prompt-feedback-drawer__section optimization-report"
      data-testid="optimization-report"
    >
      <div className="section-header">
        <div className="flex-space-between flex-align-center">
          {header}
          <OptimizationChip optimization={detail} adopted={adopted} />
        </div>
        <small className="muted-text">
          {outcomeSentence(detail)}
          {!isEmpty(STOP_REASON_LABELS[outcome?.stop_reason]) && (
            <> · {STOP_REASON_LABELS[outcome.stop_reason]}</>
          )}
        </small>
      </div>

      {hasCounts && (
        <div className="optimization-report__stats" data-testid="agent-score">
          <FeedbackStat outcome={outcome} />
          {!isNil(outcome.regression_total) && <RegressionStat outcome={outcome} />}
          <p data-testid="agent-explanation">{explanation(outcome)}</p>
          {!isNil(outcome.regression_total) && detail.winner_candidate_id && (
            <div>
              <Button link onClick={() => setShowRegressionCalls(!showRegressionCalls)}>
                {showRegressionCalls ? 'Hide' : 'See'} the calls that would change
              </Button>
            </div>
          )}
        </div>
      )}

      {showRegressionCalls && (
        <ChangedCallsList
          optimizationId={detail.optimization_id}
          candidateId={detail.winner_candidate_id}
          criteriaScid={task?.scorecard_criteria_scid}
          scope={REGRESSION_SCOPE}
          mustBePresent={mustBePresent}
        />
      )}

      {outcome?.result === CALIBRATION_RESULT.ALREADY_ADOPTED && (
        <Banner success data-testid="agent-adopted-banner">
          A person already adopted a fix (prompt #{outcome.applied_prompt_id}) and the reported
          calls now pass. If it looks right, resolve this task.
        </Banner>
      )}

      {outcome?.result === CALIBRATION_RESULT.AUTO_APPLIED && (
        <Banner success data-testid="agent-auto-applied-banner">
          This organization has auto-apply on, so the agent saved this fix to the scorecard (prompt
          #{outcome.applied_prompt_id}). If it looks right, resolve this task.
        </Banner>
      )}

      {outcome?.apply_refused && (
        <p className="muted-text" data-testid="agent-apply-refused">
          Auto-apply didn&apos;t save this one ({outcome.apply_refused}) — it needs a review.
        </p>
      )}

      {!isEmpty(detail.winning_prompt_text) && !nothingToChange && (
        <>
          <SegmentedControl
            small
            className="optimization-report__view-toggle"
            items={[
              {
                accessor: VIEW.DIFF,
                label: 'What changed',
                active: view === VIEW.DIFF,
                onClick: () => setView(VIEW.DIFF),
              },
              {
                accessor: VIEW.FULL,
                label: 'Full prompt',
                active: view === VIEW.FULL,
                onClick: () => setView(VIEW.FULL),
              },
            ]}
          />
          {view === VIEW.DIFF ? (
            <div className="optimization-report__prompt-panel">
              <UnifiedDiff
                plain
                oldText={detail.starting_prompt_text}
                newText={detail.winning_prompt_text}
                emptyLabel="The winning attempt matches the current prompt."
              />
            </div>
          ) : (
            <pre className="optimization-report__prompt-panel optimization-report__prompt-text">
              {detail.winning_prompt_text}
            </pre>
          )}
        </>
      )}

      {canUse && (
        <div className="optimization-report__use">
          <Button
            brand
            data-testid="use-agent-prompt"
            disabled={!isNil(useBlockedReason)}
            onClick={() =>
              onUsePrompt({
                userPrompt: detail.winning_prompt_text,
                model: detail.winning_model_id,
                optimizationId: detail.optimization_id,
                candidateId: detail.winner_candidate_id,
              })
            }
          >
            Use this prompt
          </Button>
          <small className="muted-text" data-testid="use-agent-prompt-note">
            {useBlockedReason ??
              [
                'Stages it in the editor above to test and save',
                gatePassed === false &&
                  'It changes more unreported calls than usual — check them first',
                outcome?.model_changed && `It also switches the model to ${outcome.model}`,
              ]
                .filter(Boolean)
                .join('. ')}
          </small>
        </div>
      )}

      {calibrateControls('Calibrate again')}
    </section>
  )
}
